import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Calendar, Users, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

// Import all campus components
import {
  PageHeader,
  StatStrip,
  MetricTile,
  StatusLight,
  BadgePill,
  EventCard,
  MemberCard,
  ActivityFeed,
  LeaderboardCard,
  LeaderboardList,
  PassCard,
  SponsorCard,
  EmptyState,
  AlertBanner,
  Modal,
  Drawer,
  SearchBar,
  FilterBar,
  Tabs,
  Table,
  QRDisplay,
} from "@/components/campus";

// Sample data
const sampleStats = [
  { label: "Total Events", value: "24", trend: "up", trendValue: "+12%" },
  { label: "Active Guests", value: "1,847", trend: "up", trendValue: "+8%" },
  { label: "Check-ins Today", value: "342", trend: "neutral" },
  { label: "Revenue", value: "$12.4k", trend: "up", trendValue: "+23%" },
  { label: "Avg. Attendance", value: "87%", trend: "down", trendValue: "-3%" },
];

const sampleEvents = [
  { name: "Spring Formal 2025", date: "Mar 15, 2025", time: "8:00 PM", location: "Grand Ballroom", capacity: 300, attendees: 287, status: "upcoming" },
  { name: "Rush Week Kickoff", date: "Jan 20, 2025", time: "6:00 PM", location: "Chapter House", capacity: 150, attendees: 150, status: "live" },
  { name: "Alumni Mixer", date: "Feb 10, 2025", time: "7:00 PM", location: "University Club", capacity: 100, attendees: 45, status: "draft" },
];

const sampleMembers = [
  { name: "Alex Thompson", email: "alex.t@university.edu", role: "president", status: "active", joinDate: "Aug 2022" },
  { name: "Sarah Chen", email: "s.chen@university.edu", role: "admin", status: "active", joinDate: "Sep 2023" },
  { name: "Mike Rodriguez", email: "m.rod@university.edu", role: "member", status: "pending", joinDate: "Jan 2025" },
];

const sampleActivities = [
  { icon: "user-add", text: "Sarah Chen added 3 new guests to Spring Formal", timestamp: "2 min ago" },
  { icon: "scan", text: "Check-in: John Doe arrived at Rush Week Kickoff", timestamp: "5 min ago" },
  { icon: "payment", text: "Payment received: $2,500 from Acme Corp sponsorship", timestamp: "1 hour ago" },
  { icon: "event", text: "Alumni Mixer event created by Alex Thompson", timestamp: "3 hours ago" },
  { icon: "success", text: "Spring Formal capacity increased to 300", timestamp: "Yesterday" },
];

const sampleLeaderboard = [
  { name: "Alpha Sigma Phi", subtitle: "Greek Row", value: "2,847", valueLabel: "check-ins" },
  { name: "Beta Theta Pi", subtitle: "North Campus", value: "2,341", valueLabel: "check-ins" },
  { name: "Delta Gamma", subtitle: "Greek Row", value: "2,156", valueLabel: "check-ins" },
  { name: "Sigma Chi", subtitle: "South Campus", value: "1,923", valueLabel: "check-ins" },
  { name: "Kappa Alpha", subtitle: "West Campus", value: "1,756", valueLabel: "check-ins" },
];

const sampleTableData = [
  { id: 1, name: "John Doe", email: "john@uni.edu", status: "Active", events: 12, lastActive: "2 min ago" },
  { id: 2, name: "Jane Smith", email: "jane@uni.edu", status: "Pending", events: 5, lastActive: "1 hour ago" },
  { id: 3, name: "Bob Wilson", email: "bob@uni.edu", status: "Active", events: 8, lastActive: "Yesterday" },
];

const sampleFilters = [
  { key: "status", label: "Status", options: [
    { value: "active", label: "Active" },
    { value: "pending", label: "Pending" },
    { value: "inactive", label: "Inactive" },
  ]},
  { key: "role", label: "Role", options: [
    { value: "admin", label: "Admin" },
    { value: "member", label: "Member" },
    { value: "guest", label: "Guest" },
  ]},
];

const sampleTabs = [
  { value: "all", label: "All", count: 24 },
  { value: "upcoming", label: "Upcoming", count: 8 },
  { value: "live", label: "Live", count: 2 },
  { value: "past", label: "Past", count: 14 },
];

// Component Section wrapper
const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-xl font-semibold text-cloud-white mb-6 pb-2 border-b border-white/[0.06]">
      {title}
    </h2>
    {children}
  </div>
);

// Component showcase wrapper
const ComponentBox = ({ label, children, fullWidth = false }) => (
  <div className={fullWidth ? "w-full" : ""}>
    <p className="text-xs text-muted-text uppercase tracking-wider mb-3">{label}</p>
    {children}
  </div>
);

export const ComponentLibrary = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [activeFilters, setActiveFilters] = useState([]);

  const handleFilterChange = (key, value) => {
    if (value) {
      setActiveFilters(prev => {
        const existing = prev.find(f => f.key === key);
        if (existing) {
          return prev.map(f => f.key === key ? { ...f, value } : f);
        }
        const filter = sampleFilters.find(f => f.key === key);
        return [...prev, { key, label: filter?.label, value }];
      });
    } else {
      setActiveFilters(prev => prev.filter(f => f.key !== key));
    }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <div className="sticky top-0 z-40 glass border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-soft-silver hover:text-cloud-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold text-cloud-white">Component Library</h1>
              <p className="text-sm text-muted-text">CamPassed Design System</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-electric-ice-blue flex items-center justify-center">
              <span className="text-obsidian font-bold">C</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Layout Components */}
        <Section title="Layout Components">
          <div className="space-y-8">
            <ComponentBox label="PageHeader" fullWidth>
              <PageHeader 
                title="Events Dashboard"
                subtitle="Manage all your organization events in one place"
                actions={
                  <>
                    <Button variant="outline" className="border-white/[0.06]">
                      <Settings className="w-4 h-4 mr-2" /> Settings
                    </Button>
                    <Button className="bg-cloud-white text-obsidian hover:bg-white/90">
                      <Plus className="w-4 h-4 mr-2" /> New Event
                    </Button>
                  </>
                }
              />
            </ComponentBox>
            
            <ComponentBox label="StatStrip" fullWidth>
              <StatStrip stats={sampleStats} />
            </ComponentBox>
            
            <ComponentBox label="MetricTile (standalone)">
              <div className="grid grid-cols-3 gap-4">
                <MetricTile label="Active Users" value="1,234" trend="up" trendValue="+15%" />
                <MetricTile label="Events Today" value="8" trend="neutral" />
                <MetricTile label="Cancellations" value="3" trend="down" trendValue="-2%" />
              </div>
            </ComponentBox>
          </div>
        </Section>

        {/* Data Display */}
        <Section title="Data Display">
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <ComponentBox label="StatusLight">
                <div className="space-y-3">
                  <StatusLight status="active" />
                  <StatusLight status="pending" />
                  <StatusLight status="live" label="Broadcasting" />
                  <StatusLight status="error" label="Offline" />
                </div>
              </ComponentBox>
              
              <ComponentBox label="BadgePill">
                <div className="flex flex-wrap gap-2">
                  <BadgePill>Default</BadgePill>
                  <BadgePill variant="primary">Primary</BadgePill>
                  <BadgePill variant="success">Success</BadgePill>
                  <BadgePill variant="warning">Warning</BadgePill>
                  <BadgePill variant="error">Error</BadgePill>
                  <BadgePill variant="muted">Muted</BadgePill>
                </div>
              </ComponentBox>
            </div>

            <ComponentBox label="EventCard" fullWidth>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {sampleEvents.map((event, i) => (
                  <EventCard key={i} {...event} />
                ))}
              </div>
            </ComponentBox>

            <ComponentBox label="MemberCard" fullWidth>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {sampleMembers.map((member, i) => (
                  <MemberCard key={i} {...member} />
                ))}
              </div>
            </ComponentBox>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ComponentBox label="ActivityFeed">
                <ActivityFeed items={sampleActivities} />
              </ComponentBox>
              
              <ComponentBox label="LeaderboardList">
                <LeaderboardList items={sampleLeaderboard} title="Top Organizations" />
              </ComponentBox>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <ComponentBox label="PassCard">
                <PassCard 
                  guestName="John Doe"
                  eventName="Spring Formal 2025"
                  eventDate="Mar 15, 2025 • 8:00 PM"
                  status="valid"
                  passId="PASS-2025-001847"
                />
              </ComponentBox>
              
              <ComponentBox label="SponsorCard">
                <SponsorCard 
                  name="Acme Corporation"
                  tier="gold"
                  dealAmount="5,000"
                  contactName="Jane Smith"
                  contactEmail="jane@acme.com"
                />
              </ComponentBox>
              
              <ComponentBox label="QRDisplay">
                <QRDisplay 
                  title="Event Check-in"
                  subtitle="Scan to enter"
                  value="PASS-2025-001847"
                  showDownload
                />
              </ComponentBox>
            </div>
          </div>
        </Section>

        {/* Feedback & Overlay */}
        <Section title="Feedback & Overlay">
          <div className="space-y-8">
            <ComponentBox label="AlertBanner" fullWidth>
              <div className="space-y-4">
                <AlertBanner variant="info" title="New Feature" message="QR scanning is now available on mobile devices." />
                <AlertBanner variant="warning" title="Capacity Warning" message="Spring Formal is at 95% capacity. Consider expanding or closing registration." />
                <AlertBanner variant="error" title="Payment Failed" message="Unable to process sponsor payment. Please check payment details." />
                <AlertBanner variant="success" title="Event Published" message="Rush Week Kickoff is now live and visible to all students." />
              </div>
            </ComponentBox>

            <ComponentBox label="EmptyState" fullWidth>
              <div className="bg-graphite rounded-xl border border-white/[0.06]">
                <EmptyState 
                  icon="events"
                  title="No events yet"
                  description="Create your first event to start managing guest lists and check-ins."
                  actionLabel="Create Event"
                  onAction={() => {}}
                />
              </div>
            </ComponentBox>

            <div className="flex gap-4">
              <ComponentBox label="Modal">
                <Button onClick={() => setModalOpen(true)} className="bg-cloud-white text-obsidian hover:bg-white/90">
                  Open Modal
                </Button>
                <Modal 
                  isOpen={modalOpen} 
                  onClose={() => setModalOpen(false)}
                  title="Create New Event"
                  footer={
                    <>
                      <Button variant="outline" onClick={() => setModalOpen(false)} className="border-white/[0.06]">
                        Cancel
                      </Button>
                      <Button className="bg-cloud-white text-obsidian hover:bg-white/90">
                        Create Event
                      </Button>
                    </>
                  }
                >
                  <p className="text-soft-silver">Modal body content goes here. This is a glass-panel overlay with backdrop blur.</p>
                </Modal>
              </ComponentBox>

              <ComponentBox label="Drawer">
                <Button onClick={() => setDrawerOpen(true)} variant="outline" className="border-white/[0.06]">
                  Open Drawer
                </Button>
                <Drawer 
                  isOpen={drawerOpen} 
                  onClose={() => setDrawerOpen(false)}
                  title="Event Details"
                >
                  <p className="text-soft-silver">Drawer panel content. Slides in from the right side of the screen.</p>
                </Drawer>
              </ComponentBox>
            </div>
          </div>
        </Section>

        {/* Controls */}
        <Section title="Controls">
          <div className="space-y-8">
            <ComponentBox label="SearchBar" fullWidth>
              <SearchBar 
                placeholder="Search events, guests, members..."
                value={searchValue}
                onChange={setSearchValue}
                showFilter
                onFilterClick={() => {}}
              />
            </ComponentBox>

            <ComponentBox label="FilterBar" fullWidth>
              <FilterBar 
                filters={sampleFilters}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                onClearAll={() => setActiveFilters([])}
              />
            </ComponentBox>

            <ComponentBox label="Tabs" fullWidth>
              <Tabs 
                tabs={sampleTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
              />
            </ComponentBox>

            <ComponentBox label="Table" fullWidth>
              <Table 
                columns={[
                  { key: "name", label: "Name", width: "25%" },
                  { key: "email", label: "Email", width: "30%" },
                  { key: "status", label: "Status", width: "15%", render: (val) => <BadgePill variant={val === "Active" ? "success" : "warning"}>{val}</BadgePill> },
                  { key: "events", label: "Events", width: "15%", align: "center", mono: true },
                  { key: "lastActive", label: "Last Active", width: "15%", align: "right", mono: true },
                ]}
                data={sampleTableData}
              />
            </ComponentBox>
          </div>
        </Section>

        {/* Color Palette Reference */}
        <Section title="Color Palette">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {[
              { name: "Obsidian", color: "#0A0A0B", text: "white" },
              { name: "Graphite", color: "#16181D", text: "white" },
              { name: "Cloud White", color: "#F5F7FA", text: "black" },
              { name: "Soft Silver", color: "#C9CED6", text: "black" },
              { name: "Electric Ice", color: "#4DA3FF", text: "black" },
              { name: "Cool Mint", color: "#5EEAD4", text: "black" },
              { name: "Soft Amber", color: "#F7C873", text: "black" },
              { name: "Signal Red", color: "#FF5A5F", text: "white" },
            ].map((c) => (
              <div key={c.name} className="text-center">
                <div 
                  className="w-full h-16 rounded-lg border border-white/[0.06] mb-2"
                  style={{ backgroundColor: c.color }}
                />
                <p className="text-xs text-soft-silver">{c.name}</p>
                <p className="text-xs font-mono text-muted-text">{c.color}</p>
              </div>
            ))}
          </div>
        </Section>

      </div>
    </div>
  );
};

export default ComponentLibrary;
